import React, { useEffect, useMemo, useState } from 'react';
import { Button, Grid, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _isEmpty from 'lodash/isEmpty';
import Modal from '../common/Modal';
import Steps from '../common/Steps';
import { AssignDates } from './AssignDates';
import { AssignFlow } from './AssignFlow';
import { showProgress } from '../../redux/actions/DealershipActions';
import { ErrorToast, MultipleErrorToast, notifySuccess } from '../utils/Utils';
import service from '../../apis/service';
import { apiEndpoints } from '../../constants/apiEndpoints';

const useStyle = makeStyles({
  popupContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
});

export function AssignCertPopup({ isAssignOpen, setIsAssignOpen }) {
  const classes = useStyle();
  const dispatch = useDispatch();

  const selectedCertInfo = useSelector((state) => state.certificateData.selectedCertInfo);

  const [step, setStep] = useState(1);
  const [assignPayload, setAssignPayload] = useState({});
  const [endDate, setEndDate] = useState(null);
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});

  const getFilters = async () => {
    dispatch(showProgress(true));
    try {
      const res = await service.get(apiEndpoints.DEALER_FILTER);
      setFilters(res || {});
    } catch (error) {
      ErrorToast(error);
    }

    dispatch(showProgress(false));
  };

  useEffect(() => {
    getFilters();
  }, []);

  const DEFAULT_FILTERS = useMemo(() => {
    const sales = filters?.salesPipelineStatus?.find((i) => i.title === 'Active') || null;
    return {
      salesPipelineStatus: sales ? [sales] : [],
      source: ['ADMIN'],
      dealerships: [],
      dealerGroup: [],
      plans: [filters?.plans?.find((item) => item.id === 2)],
      pxOwners: filters?.pxOwners?.map((item) => item) || [],
    };
  }, [filters]);

  useEffect(() => {
    setAppliedFilters(DEFAULT_FILTERS);
  }, [DEFAULT_FILTERS]);

  const onNext = async () => {
    if (!checkValidation()) return;
    if (step === 1) {
      setStep((pre) => pre + 1);
    } else if (_isEmpty(assignPayload)) {
      ErrorToast('Please select atleast one dealership.');
    } else {
      dispatch(showProgress(true));
      const payload = {
        certificationId: selectedCertInfo.id,
        endDate: moment(endDate).format('YYYY-MM-DD'),
        dealerships: assignPayload,
      };
      try {
        await service.post(apiEndpoints.CERT_ASSIGN, payload);
        notifySuccess('Certification assigned successfully');
        setIsAssignOpen(false);
      } catch (error) {
        ErrorToast(error);
      }
      dispatch(showProgress(false));
    }
  };

  const checkValidation = () => {
    const errors = [];
    let isValid = true;
    if (step === 1) {
      if (!endDate) {
        isValid = false;
        errors.push('Please select a due date');
      }
    }
    if (errors.length !== 0) {
      MultipleErrorToast(errors);
    }
    return isValid;
  };

  return (
    <Modal isOpen={isAssignOpen} height="88vh" width="92vw">
      <Grid className={classes.popupContainer}>
        <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
          <Grid item>
            <Grid item style={{ fontWeight: '700', fontSize: '18px' }}>
              Assign Certification
            </Grid>
          </Grid>
          <Grid item>
            <CloseIcon onClick={() => setIsAssignOpen(false)} style={{ cursor: 'pointer' }} />
          </Grid>
        </Grid>
        <Steps step={step} stepInfo={['Select Date', 'Assign To']} />

        {step === 1 && <AssignDates endDate={endDate} setEndDate={setEndDate} />}
        {step === 2 && (
          <AssignFlow
            assignPayload={assignPayload}
            setAssignPayload={setAssignPayload}
            endDate={endDate}
            filters={filters}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            DEFAULT_FILTERS={DEFAULT_FILTERS}
          />
        )}

        <Grid style={{ display: 'flex' }}>
          {step > 1 && (
            <Button onClick={() => setStep((pre) => pre - 1)} variant="outlined">
              Back
            </Button>
          )}
          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            <>
              <Button onClick={() => setIsAssignOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button variant="contained" color="primary" style={{ marginLeft: '10px' }} onClick={onNext}>
                {step !== 2 ? 'Next' : 'Save'}
              </Button>
            </>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}
